// @flow

import React, { PureComponent } from 'react';
import { createPaginationContainer, graphql } from 'react-relay';
import debounce from 'lodash.debounce';
import { BrowserUtils } from 'shared/utils';
import LoadingMore from 'shared/components/common/LoadingMore';
import EmptySearchResults from 'shared/components/common/EmptySearchResults';
import JournalEntriesTable from './JournalEntriesTable';

type Props = {
  history: Object,
  filtersDirty: boolean,
  onCreateNew: () => void,
  onViewDetail: (entry: Object) => void,
  journalEntries: Object,
  relay: {
    environment: Object,
    hasMore: () => boolean,
    isLoading: () => boolean,
    loadMore: (pageSize: number, callback: Function) => void,
  },
};

class PaginatedJournalEntriesContainer extends PureComponent<Props> {
  constructor(props: Props) {
    super(props);
    this.onScrollEndReached = debounce(this.onScrollEndReached, 100);
  }

  componentDidMount = () => {
    document.addEventListener('scroll', this.onScrollEndReached);
  };

  componentWillUnmount = () => {
    document.removeEventListener('scroll', this.onScrollEndReached);
  };

  onScrollEndReached = () => {
    const { relay } = this.props;
    if (BrowserUtils.scrollEndReached(200)) {
      if (relay.hasMore()) {
        relay.loadMore(30);
      }
    }
  };

  render() {
    const {
      history,
      onCreateNew,
      onViewDetail,
      journalEntries,
      relay,
      filtersDirty,
    } = this.props;

    if (
      (!journalEntries.journalEntries.edges ||
        !journalEntries.journalEntries.edges.length) &&
      filtersDirty
    ) {
      return <EmptySearchResults />;
    }

    return (
      <>
        <JournalEntriesTable
          history={history}
          onCreateNew={onCreateNew}
          onViewDetail={onViewDetail}
          journalEntryEdges={journalEntries.journalEntries.edges}
        />
        {relay.hasMore() && relay.isLoading() ? <LoadingMore /> : null}
      </>
    );
  }
}

export default createPaginationContainer(
  PaginatedJournalEntriesContainer,
  {
    journalEntries: graphql`
      fragment PaginatedJournalEntriesContainer_journalEntries on Query
        @argumentDefinitions(
          count: { type: "Int", defaultValue: 30 }
          cursor: { type: "String" }
          search: { type: "String" }
          sortBy: { type: "JournalEntrySort" }
          statuses: { type: "[JournalEntryStatus!]" }
          sourceTypes: { type: "[JournalSourceType!]" }
          startDate: { type: "Date" }
          endDate: { type: "Date" }
        ) {
        journalEntries(
          first: $count
          after: $cursor
          search: $search
          sortBy: $sortBy
          statuses: $statuses
          sourceTypes: $sourceTypes
          startDate: $startDate
          endDate: $endDate
        )
          @connection(
            key: "PaginatedJournalEntriesContainer_journalEntries"
            filters: []
          ) {
          edges {
            cursor
            ...JournalEntriesTable_journalEntryEdges
          }
        }
      }
    `,
  },
  {
    query: graphql`
      query PaginatedJournalEntriesContainerQuery(
        $count: Int!
        $cursor: String
        $search: String
        $sortBy: JournalEntrySort
        $statuses: [JournalEntryStatus!]
        $sourceTypes: [JournalSourceType!]
        $startDate: Date
        $endDate: Date
      ) {
        ...PaginatedJournalEntriesContainer_journalEntries
          @arguments(
            count: $count
            cursor: $cursor
            search: $search
            sortBy: $sortBy
            statuses: $statuses
            sourceTypes: $sourceTypes
            startDate: $startDate
            endDate: $endDate
          )
      }
    `,
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }, fragmentVariables) {
      return { ...fragmentVariables, count, cursor };
    },
  },
);
